import { useState, useEffect, useCallback } from 'react'
import { useAppSelector } from '@state/hooks'
import './PortfolioDashboard.css'

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000'

interface CompanySummary {
  id: number
  name: string
  industry?: string
  health_score: number
  status: 'healthy' | 'at_risk' | 'critical'
  attention_score?: number
  components: {
    execution: number
    alerts: number
    vitals: number
  }
}

interface PortfolioData {
  portfolio_health: number
  total_companies: number
  ranked_companies: CompanySummary[]
}

export default function PortfolioDashboard() {
  const { user } = useAppSelector(state => state.auth)
  const [data, setData] = useState<PortfolioData | null>(null)
  const [alerts, setAlerts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState<'grid' | 'list'>('grid')

  const userId = user?.id || 1

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('jwt_token')
      const authHeaders: Record<string, string> = token ? { 'Authorization': `Bearer ${token}` } : {}
      const [dashRes, alertRes] = await Promise.all([
        fetch(`${API_BASE}/portfolio/${userId}/dashboard`, { headers: authHeaders }),
        fetch(`${API_BASE}/portfolio/${userId}/alerts`, { headers: authHeaders }),
      ])
      if (dashRes.ok) {
        const d = await dashRes.json()
        setData(d)
      }
      if (alertRes.ok) {
        const a = await alertRes.json()
        setAlerts(a.alerts || [])
      }
    } catch (e) {
      console.error('Portfolio load failed:', e)
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => { loadData() }, [loadData])

  const getHealthColor = (score: number) => {
    if (score >= 70) return 'var(--health-good, #2e7d32)'
    if (score >= 40) return 'var(--health-warn, #f57f17)'
    return 'var(--health-critical, #c62828)'
  }

  const getStatusIcon = (status: string) => {
    if (status === 'healthy') return '\u2705'
    if (status === 'at_risk') return '\u26A0\uFE0F'
    return '\u{1F6A8}'
  }

  if (loading) {
    return <div className="portfolio-dashboard"><div className="portfolio-loading">Loading portfolio...</div></div>
  }

  if (!data) {
    return <div className="portfolio-dashboard"><div className="portfolio-empty">No portfolio data available.</div></div>
  }

  return (
    <div className="portfolio-dashboard">
      {/* Header */}
      <div className="portfolio-header">
        <div className="portfolio-title">
          <a href="/" className="back-to-ise">&larr; Back to Causeway</a>
          <h1>Portfolio Dashboard</h1>
          <span className="company-count">{data.total_companies} companies</span>
        </div>
        <div className="portfolio-health-summary">
          <div className="health-ring" style={{ borderColor: getHealthColor(data.portfolio_health) }}>
            <span className="health-number">{data.portfolio_health}</span>
          </div>
          <span className="health-label">Portfolio Health</span>
        </div>
        <div className="view-toggle">
          <button className={view === 'grid' ? 'active' : ''} onClick={() => setView('grid')}>Grid</button>
          <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}>List</button>
        </div>
      </div>

      {/* Company Cards */}
      <div className={`company-cards ${view}`}>
        {data.ranked_companies.map(company => (
          <div key={company.id} className={`company-card status-${company.status}`}>
            <div className="card-header">
              <span className="status-icon">{getStatusIcon(company.status)}</span>
              <h3>{company.name}</h3>
              {company.industry && <span className="industry-tag">{company.industry}</span>}
            </div>
            <div className="card-health">
              <div className="health-bar">
                <div className="health-fill" style={{ width: `${company.health_score}%`, background: getHealthColor(company.health_score) }} />
              </div>
              <span className="health-score">{company.health_score}/100</span>
            </div>
            <div className="card-components">
              <div className="component">
                <span className="comp-label">Execution</span>
                <span className="comp-value">{company.components.execution}</span>
              </div>
              <div className="component">
                <span className="comp-label">Alerts</span>
                <span className="comp-value">{company.components.alerts}</span>
              </div>
              <div className="component">
                <span className="comp-label">Vitals</span>
                <span className="comp-value">{company.components.vitals}</span>
              </div>
            </div>
            {company.attention_score !== undefined && company.attention_score > 50 && (
              <div className="attention-badge">Needs attention</div>
            )}
          </div>
        ))}
      </div>

      {/* Alerts Feed */}
      {alerts.length > 0 && (
        <div className="portfolio-alerts">
          <h2>Active Alerts ({alerts.length})</h2>
          <div className="alerts-list">
            {alerts.slice(0, 10).map((alert, idx) => (
              <div key={idx} className={`alert-item severity-${alert.severity}`}>
                <span className="alert-company">{alert.company_name}</span>
                <span className="alert-title">{alert.title}</span>
                <span className={`alert-severity ${alert.severity}`}>{alert.severity}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
